import { useApi } from '../useApi'
import { Link } from '../router'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Rows, Stat } from '../components/bits'

interface Impact {
  observed: {
    hours_total: number
    hours_this_month: number
    sites_listening_7d: number
    sites_total: number
    by_month: { month: string; hours: number }[]
    by_site: { site: string; locality_no: number | null; hours: number; latest: string | null }[]
  }
  integrity: {
    deployments_counted: number
    absolute_capable: number
    blocking: { term: string; deployments: number }[]
    computes_missing_full_scale: number
    computes_total: number
    missions_missing_gain: number
    missions_counted: number
    certificates: number
    microphones_calibrated: number
    microphones_total: number
  }
  frequency_axis: { checked: boolean; present: boolean; bands: number | null; key: string | null }
  outcome: {
    computed: boolean
    minutes: number
    disturbed_minutes?: number
    disturbed_share?: number | null
    quiet_share?: number | null
    basis?: string
    comparable_across_sites?: boolean
    sites: { site: string; minutes: number; disturbed_share: number; quiet_share: number; quiet_floor: number | null }[]
  }
  unlocks: { id: string; blocked: boolean; cost: string; title: string; detail: string }[]
  indicator: { name: string; bands_hz: number[]; needs: string[] }
}

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)}%`

/** What Phoniq can honestly say about the ocean.
 *
 *  Deliberately built the other way up from a usual dashboard. The numbers
 *  come second and what stands behind them comes first, because the honest
 *  answer today is that almost nothing is claimable yet — and a page that
 *  opened with a confident trend would be inventing the number calibration.py
 *  exists to refuse.
 *
 *  Only missions marked `deployment` count. The first version of this page,
 *  before that distinction existed, reported the fleet as 91% "acoustically
 *  disturbed" — computed entirely from two nodes sitting in a house.
 */
export function ImpactPage() {
  const q = useApi<Impact>('/v1/impact')
  const d = q.data

  if (q.cold) return <Rows n={8} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!d) return null

  const noOcean = d.integrity.deployments_counted === 0

  return (
    <>
      <PageHead
        title="Ocean impact"
        lead="Measured sound, and an honest account of what it can and cannot yet support."
      />

      {noOcean ? (
        <div className="card flush" style={{ marginBottom: 20 }}>
          <Empty
            title="Nothing in the sea yet"
            action={<Link className="btn" to="/add">Register a deployment</Link>}
          >
            Every mission so far is marked as a test, so nothing here counts
            towards what Phoniq claims about the ocean. That is the intended
            behaviour rather than an empty state: a node on a bench records real
            audio and would otherwise be indistinguishable from one under a cage.
            Mark a mission as a deployment and it starts counting.
          </Empty>
        </div>
      ) : (
        <>
          <div className="grid c3" style={{ marginBottom: 20 }}>
            <div className="card">
              <Stat label="Ocean-hours observed" value={d.observed.hours_total.toLocaleString()} />
            </div>
            <div className="card">
              <Stat label="This month" value={d.observed.hours_this_month.toLocaleString()} small />
            </div>
            <div className="card">
              <Stat label="Sites listening" small
                value={`${d.observed.sites_listening_7d} of ${d.observed.sites_total}`} />
            </div>
          </div>

          {d.outcome.computed && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h2 className="display" style={{ fontSize: 17, marginBottom: 4 }}>
                What we heard
              </h2>
              {/* Said before the numbers, not after them. The share is a true
                  statement about change at one place and nothing else. */}
              <p className="hint" style={{ marginBottom: 14 }}>
                From the node’s own minute-by-minute classification, against each
                site’s recent history. {d.outcome.basis} — a site may be compared
                with itself over time, never with another site, and never against
                a threshold for harm.
              </p>
              <div className="grid c2" style={{ marginBottom: 14 }}>
                <Stat label="Acoustically disturbed" value={pct(d.outcome.disturbed_share)} small />
                <Stat label="Quiet" value={pct(d.outcome.quiet_share)} small />
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Site</th><th className="r">Minutes</th>
                    <th className="r">Disturbed</th><th className="r">Quiet floor</th>
                  </tr>
                </thead>
                <tbody>
                  {d.outcome.sites.map((s) => (
                    <tr key={s.site}>
                      <td data-l="Site">{s.site}</td>
                      <td data-l="Minutes" className="r num">{s.minutes.toLocaleString()}</td>
                      <td data-l="Disturbed" className="r num">{pct(s.disturbed_share)}</td>
                      {/* dBFS. Shown because its trend is the ambient-creep
                          signature; the column header does not call it a sea
                          level, because it is not one. */}
                      <td data-l="Quiet floor" className="r num">
                        {s.quiet_floor === null ? '—' : `${s.quiet_floor} dBFS`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <h2 className="display" style={{ fontSize: 17, marginBottom: 4 }}>
          What would make this mean more
        </h2>
        <p className="hint" style={{ marginBottom: 16 }}>
          Working towards {d.indicator.name} — the {d.indicator.bands_hz.join(' and ')} Hz
          third-octave bands, the published indicator Norway reports on through
          OSPAR. In order of cost.
        </p>

        <div className="stack" style={{ gap: 12 }}>
          {d.unlocks.map((u) => (
            <div key={u.id} className="unlock">
              <div className="unlock-head">
                <strong>{u.title}</strong>
                <span className={`badge${u.blocked ? '' : ' on'}`}>
                  {u.blocked ? u.cost : 'available now'}
                </span>
              </div>
              <p>{u.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid c2">
        <div className="card">
          <h2 className="display" style={{ fontSize: 17, marginBottom: 14 }}>
            Measurement integrity
          </h2>
          <dl className="dl">
            <dt>Can state dB re 1 µPa</dt>
            <dd className="num">
              {d.integrity.absolute_capable} of {d.integrity.deployments_counted} deployments
            </dd>
            <dt>Boards without full-scale voltage</dt>
            <dd className="num">
              {d.integrity.computes_missing_full_scale} of {d.integrity.computes_total}
            </dd>
            <dt>Deployments without analogue gain</dt>
            <dd className="num">
              {d.integrity.missions_missing_gain} of {d.integrity.missions_counted}
            </dd>
            <dt>Calibration certificates</dt>
            <dd className="num">
              {d.integrity.certificates} on {d.integrity.microphones_calibrated} of{' '}
              {d.integrity.microphones_total} hydrophones
            </dd>
          </dl>
          {d.integrity.blocking.length > 0 && (
            <div className="notice" style={{ marginTop: 14 }}>
              Blocking most deployments: <strong>{d.integrity.blocking[0]!.term}</strong>
              {' '}({d.integrity.blocking[0]!.deployments}).
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="display" style={{ fontSize: 17, marginBottom: 14 }}>
            Frequency axis
          </h2>
          <p className="hint" style={{ marginBottom: 12 }}>
            Band levels arrive without the edges that say which frequencies they
            cover. Until they carry them, nothing can separate the part of the
            sound salmon and cod hear from the part they do not — and that
            reading needs no calibration, only the axis.
          </p>
          <dl className="dl">
            <dt>Present in the latest roll-up</dt>
            <dd>{d.frequency_axis.checked
              ? (d.frequency_axis.present
                  ? `yes — ${d.frequency_axis.bands} edges`
                  : 'no')
              : 'could not be checked'}</dd>
            {d.frequency_axis.key && (
              <>
                <dt>Checked</dt>
                <dd className="mono faint">{d.frequency_axis.key}</dd>
              </>
            )}
          </dl>
        </div>
      </div>
    </>
  )
}
